import React, { useMemo } from 'react';
import { BrandDetail } from '@/apis/core';

import {
  faCircleUser,
  faBuilding,
  faLocationDot,
  faAddressBook,
} from '@fortawesome/pro-regular-svg-icons';

import { DetailsSection, InfoRow, Divider } from './components';

const DetailsTab = ({
  brand,
  verticalTypes,
}: {
  brand: BrandDetail;
  verticalTypes: any;
}) => {
  const { csp } = brand;

  const verticalDisplayName = useMemo(() => {
    if (!verticalTypes || !brand.vertical) return brand.vertical;
    const matched = verticalTypes.find((v: any) => v.id === brand.vertical);
    return matched ? matched.displayName : brand.vertical;
  }, [verticalTypes, brand.vertical]);

  return (
    <>
      <DetailsSection
        title="CSP Details"
        icon={faCircleUser}
        data-testid="cspDetailsSection"
      >
        <InfoRow>
          <InfoRow.Item label="CSP Legal Name" data-testid="cspLegalName">
            {csp.companyName}
          </InfoRow.Item>
          <InfoRow.Item label="Email Address" data-testid="cspEmail">
            {csp.email}
          </InfoRow.Item>
          <InfoRow.Item label="CSP ID" data-testid="cspId">
            {csp.uid}
          </InfoRow.Item>
          <InfoRow.Item label="Phone Number" data-testid="cspPhone">
            {csp.phone}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>

      <Divider />

      <DetailsSection
        title="Overview"
        icon={faBuilding}
        data-testid="overviewSection"
      >
        <InfoRow>
          <InfoRow.Item label="Stock Symbol" data-testid="stockSymbol">
            {brand.stockSymbol}
          </InfoRow.Item>
          <InfoRow.Item label="Website" data-testid="website">
            {brand.website}
          </InfoRow.Item>
          <InfoRow.Item
            label="Government Entity"
            data-testid="governmentEntity"
          >
            {!!brand.optionalAttributes?.governmentEntity ? 'True' : 'False'}
          </InfoRow.Item>
        </InfoRow>
        <InfoRow>
          <InfoRow.Item label="Stock Exchange" data-testid="stockExchange">
            {brand.stockExchange}
          </InfoRow.Item>
          <InfoRow.Item label="Vertical" data-testid="vertical">
            {verticalDisplayName}
          </InfoRow.Item>
          <InfoRow.Item label="Support Phone" data-testid="supportPhone">
            {brand.phone}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>

      <Divider />

      <DetailsSection
        title="Address"
        icon={faLocationDot}
        data-testid="addressSection"
      >
        <InfoRow>
          <InfoRow.Item label="Street" data-testid="street">
            {brand.street}
          </InfoRow.Item>
          <InfoRow.Item label="City" data-testid="city">
            {brand.city}
          </InfoRow.Item>
          <InfoRow.Item label="State/Region" data-testid="state">
            {brand.state}
          </InfoRow.Item>
        </InfoRow>
        <InfoRow>
          <InfoRow.Item label="Country" data-testid="country">
            {brand.country}
          </InfoRow.Item>
          <InfoRow.Item label="Postal Code" data-testid="postalCode">
            {brand.postalCode}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>

      <Divider />

      <DetailsSection
        title="Contact Information"
        icon={faAddressBook}
        data-testid="contactInformationSection"
      >
        <InfoRow>
          <InfoRow.Item label="Name" data-testid="contactName">
            {brand.businessContactFirstName || brand.businessContactLastName
              ? `${brand.businessContactFirstName} ${brand.businessContactLastName}`.trim()
              : ''}
          </InfoRow.Item>
          <InfoRow.Item label="Title" data-testid="contactTitle">
            {brand.businessContactTitle}
          </InfoRow.Item>
          <InfoRow.Item label="Email" data-testid="contactEmail">
            {brand.businessContactEmail}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>
    </>
  );
};

export default DetailsTab;
